/**
 * The designer's half of a process diagram's visual language.
 *
 * The language ITSELF — box shapes, kind colours, arrow colours, and where an
 * arrow attaches to a box — lives in the runtime package and is re-exported
 * here, because the same picture is drawn on four surfaces and only one of them
 * is this designer. Keeping a second copy here is what let the runtime's
 * diagram drift into faint borders and arrows that appeared to hang in space.
 *
 * What stays is authoring-only: the snap grid, which arrow kinds a person may
 * draw, and which one a new connection should be.
 */

import type { NodeKind } from '@/api/processflow'

export {
  NODE_W, NODE_H, GRID, KIND_STYLE, KIND_CAPTION, EDGE_STYLE, edgeGeometry, clip,
  NodeOutline, EdgeArrowDefs, GridBackdrop,
} from '@twasta/page-runtime/txn/flow/shapes'
export type { KindStyle, EdgeStyle, Box } from '@twasta/page-runtime/txn/flow/shapes'

import { GRID } from '@twasta/page-runtime/txn/flow/shapes'

/** The four arrows a designer draws. The rest are derived from the models every
 *  time the diagram is opened, and are never persisted. */
export const AUTHORED_EDGE_KINDS = ['work', 'link', 'update', 'read'] as const

export function snap(v: number): number {
  return Math.max(0, Math.round(v / GRID) * GRID)
}

/** Which arrow a new connection between these two kinds should be.
 *
 *  Drawing into a store means "this updates stock" every single time, so that
 *  is what it becomes; the designer changes it in one click if it isn't. */
export function defaultEdgeKind(fromKind: NodeKind, toKind: NodeKind): string {
  if (toKind === 'store') return 'update'
  if (toKind === 'entity') return 'read'
  if (toKind === 'external') return 'link'
  if (fromKind === 'store' || fromKind === 'entity') return 'read'
  return 'work'
}
